import React, { useMemo } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { AppLayout } from 'components/Layout/AppLayout';
import { NoAccess } from 'components/Modals/NoAccessModal/NoAccessModal';
import Permission from 'components/Permission/Permission';
import {
  type PermissionConstraint,
  type Role,
} from 'components/Permission/permission.types';
import LoginView from 'views/Login/Login';
import LoggingOutScreen from 'views/LoggingOut/LoggingOutScreen';
import { CustomerSelectionView } from 'views/Settings/CustomerSelectionView/CustomerSelectionView';
import {
  hasRequiredRole,
  isInEnabled,
  isEnabledForEnvironment,
  mergeNavigationWithDbConfig,
} from './utils';
import { useAppSelector } from 'store/hook';
import { CustomerSettingView } from 'views/Settings/Customer/CustomerSettingsView';
import { NoAccessView } from 'views/NoAccess';
import CustomerNavConfigurator from 'views/Settings/NavigationMap/NavigationMapView';
import { GetProView } from 'views/GetPro/GetPro';
import HomeView from 'views/Home/HomeView';
import { usePageTracking } from 'hooks/usePageTracking';
import { SingleFadeLoader } from 'components/SingleFadeLoader';

interface RouteConfig {
  path: string;
  label: string;
  Component: React.ReactElement;
  roles: Role[];
  type?: PermissionConstraint;
}

const MainRouterNew = (): JSX.Element => {
  usePageTracking();
  const navigationConfigLoading = useAppSelector(
    (state) => state.navigationConfigReducer.loading
  );
  const navigationConfigData = useAppSelector(
    (state) => state.navigationConfigReducer.data
  );
  const role = useAppSelector((state) => state.authReducer.role);
  const customerCode = useAppSelector(
    (state) => state.authReducer.customer.code
  );
  const user = useMemo(() => {
    return {
      customerCode,
      roles: role ? [role] : [],
    };
  }, [customerCode, role]);

  const mergedStaticAndDbConfig = useMemo(() => {
    const temp = mergeNavigationWithDbConfig(navigationConfigData);
    return temp;
  }, [navigationConfigData]);

  // Flatten the navigation map structure into a single array of routes
  const allRoutes = useMemo<RouteConfig[] | null>(() => {
    const allRoutes: RouteConfig[] = [];
    if (user.roles.length === 0 || !user.customerCode) {
      return null;
    }
    // Process each section of the navigation map
    mergedStaticAndDbConfig.forEach((section) => {
      // primary check if enabled in web or not
      // check if the parent section route is accessible based on role
      const isVisibleByRole = hasRequiredRole(user.roles, section.roles);
      if (!isVisibleByRole) {
        return; // Skip adding routes for this section if it's not visible
      }
      const isEnabled = isInEnabled(section.enabledFor, user?.customerCode);
      if (
        section?.enabledFor &&
        section?.enabledFor?.length > 0 &&
        !isEnabled
      ) {
        return;
      }
      // Check if the section is enabled for the current environment
      const isEnabledInEnv = isEnabledForEnvironment(
        section.enabledEnvironments
      );
      if (!isEnabledInEnv) {
        return; // Skip adding routes for this section if it's not enabled in current environment
      }
      // Add routes from children if they exist
      if (section.children && section.children.length > 0) {
        section.children.forEach((child) => {
          const isVisibleByRole = hasRequiredRole(user.roles, child.roles);
          if (!isVisibleByRole) {
            return; // Skip adding routes for this child if it's not visible
          }
          const isEnabled = isInEnabled(child.enabledFor, user?.customerCode);
          if (
            child?.enabledFor &&
            child?.enabledFor?.length > 0 &&
            !isEnabled
          ) {
            return;
          }
          // Check if the child route is enabled for the current environment
          const isEnabledInEnv = isEnabledForEnvironment(
            child.enabledEnvironments
          );
          if (!isEnabledInEnv) {
            return; // Skip adding routes for this child if it's not enabled in current environment
          }
          if (child.component) {
            allRoutes.push({
              path: `/${section.path}/${child.path}`,
              label: child.label,
              Component: child?.component,
              roles: child.roles ?? [],
              // Add type if it exists in your data structure
              type: undefined,
            });
          }
        });
      } else if (section.path && section.component) {
        const isVisibleByRole = hasRequiredRole(user.roles, section.roles);

        if (!isVisibleByRole) {
          return; // Skip adding routes for this section if it's not visible
        }
        const isEnabled = isInEnabled(section.enabledFor, user?.customerCode);
        if (
          section?.enabledFor &&
          section?.enabledFor?.length > 0 &&
          !isEnabled
        ) {
          return;
        }
        // Check if the section is enabled for the current environment
        const isEnabledInEnv = isEnabledForEnvironment(
          section.enabledEnvironments
        );
        if (!isEnabledInEnv) {
          return; // Skip adding routes for this section if it's not enabled in current environment
        }
        allRoutes.push({
          path: `/${section.path}`,
          label: section.label,
          Component: section?.component,
          roles: section.roles ?? [],
          // Add type if it exists in your data structure
          type: undefined,
        });
      }

      // We don't add the section itself since newNavigationMap doesn't have components at the top level
    });
    return allRoutes;
  }, [user, mergedStaticAndDbConfig]);

  const routesResolved =
    allRoutes !== null &&
    !navigationConfigLoading &&
    Boolean(role) &&
    Boolean(customerCode);

  const firstEnabledPath = useMemo(() => {
    if (!routesResolved) return null;
    if (allRoutes.length === 0) return '/no-access';
    return '/modules';
  }, [routesResolved, allRoutes]);

  return (
    <Routes>
      {/* Add any non-protected routes here */}
      <Route
        path="/"
        element={<LoginView firstEnabledPath={firstEnabledPath} />}
      />
      <Route
        path={'/login'}
        element={<LoginView firstEnabledPath={firstEnabledPath} />}
      />
      <Route path={'/logging-out'} element={<LoggingOutScreen />} />
      <Route path={'/no-access'} element={<NoAccessView />} />

      <Route
        path={'/customer-selection'}
        element={
          <Permission
            roles={[
              'director',
              'internal-user',
              'external-director',
              'external-superadmin',
            ]}
            fallbackComponent={<NoAccess />}
          >
            <CustomerSelectionView />
          </Permission>
        }
      />
      <Route
        path={'/customer-settings'}
        element={
          <Permission
            roles={['director', 'external-director']}
            fallbackComponent={<NoAccess />}
          >
            <CustomerSettingView />
          </Permission>
        }
      />

      <Route
        path={'/nav-config'}
        element={
          <Permission roles={['director']} fallbackComponent={<NoAccess />}>
            <CustomerNavConfigurator />
          </Permission>
        }
      />

      <Route path="/modules" element={<HomeView />} />
      <Route path="/home" element={<Navigate to="/modules" replace />} />

      {/* Protected routes using AppLayout */}
      <Route element={routesResolved ? <AppLayout /> : <SingleFadeLoader />}>
        <Route path="/get-pro" element={<GetProView />} />
        {Array.isArray(allRoutes) &&
          allRoutes?.map(({ path, roles, type, Component }): JSX.Element => {
            return (
              <Route
                path={path}
                key={path}
                element={
                  <Permission
                    roles={roles}
                    type={type}
                    fallbackComponent={<NoAccess />}
                  >
                    {Component}
                  </Permission>
                }
              />
            );
          })}

        {/* Only redirect unknown paths once routes are resolved */}
        {routesResolved && (
          <Route path="*" element={<Navigate to="/no-access" replace />} />
        )}
      </Route>
    </Routes>
  );
};

export default MainRouterNew;
